import { withNamespaces } from "react-i18next";
import { Text, View, Link } from "@react-pdf/renderer";
import styles from "../../Styles";
import HandIcon from "../../components/HandIcon";
import PdfIcon from "../../components/PdfIcon"; // create this file (below)

/**
 * IMPORTANT:
 * baseUrl must be the REAL domain where /document/:id/file is served.
 * If it is the same domain as the web app, window.location.origin is OK.
 * If it's a different API domain, use your config/env instead.
 */
const ABSOLUTE_BASE_URL =
  (typeof window !== "undefined" && window.location?.origin) || "";

const safeJson = (s) => {
  try {
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

const toAbsoluteUrl = (href, baseUrl) => {
  if (!href) return null;
  if (/^https?:\/\//i.test(href)) return href;

  // Fix relative URL bug in file:// PDF viewer
  const b = (baseUrl || "").replace(/\/$/, "");
  const h = href.startsWith("/") ? href : `/${href}`;
  return b ? `${b}${h}` : href; // if no baseUrl, at least return href
};

const LegalDocuments = ({ t, company }) => {
  const documentsJson = safeJson(company?.officialDocumentData);
  const items = documentsJson?.items || [];

  const legalDocuments = items.map((doc) => {
    const href = doc?._links?.file?.href || doc?._links?.self?.href || null;

    const title = `${doc.court}-${doc.register}-${doc.number}-${
      doc.type
    }-${doc.date?.slice(0, 10)}`;

    return {
      id: String(doc?.id ?? title),
      title,
      url: toAbsoluteUrl(href, ABSOLUTE_BASE_URL),
    };
  });

  const notes = [
    "Illustration Only: The specific datasets shown in this report (e.g., company data, employee numbers, financial figures) are fictitious or selected for demonstration purposes only. They are intended solely to give you an impression of how the information will be presented in the final report.",
    "No Guarantee: The actual reports generated for you will contain the relevant datasets depending on the current availability of the data from the respective content partners. There is no guarantee that every dataset shown here will be available in the same form for every target company.",
    "Data Accuracy: The accuracy, completeness, and timeliness of the actual datasets are subject to the respective content partners.",
    "Confidentiality and Non-Transferability: This example report is confidential and intended only for your internal evaluation.",
    "The onward transmission or duplication of this report to third parties (regardless of whether they are competitors or other external parties) is not permitted.",
  ];

  return (
    <View style={styles.section} break>
      {/* Section title */}
      <Text style={styles.sectionSeparator}>
        {`${t("6.")} ${t("Legal Documents")}`}
      </Text>
      {/* ICON GRID (like your target screenshot) */}
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          marginTop: 10,
          marginBottom: 16,
        }}
      >
        {legalDocuments.length > 0 ? (
          legalDocuments.map((doc) => (
            <View
              key={doc.id}
              style={{
                width: "20%",
                padding: 6,
                alignItems: "center",
              }}
            >
              {doc.url ? (
                <>
                  <Link src={doc.url}>
                    <PdfIcon />
                  </Link>

                  <Link
                    src={doc.url}
                    style={{
                      fontSize: 8,
                      textDecoration: "underline",
                      textAlign: "center",
                      lineHeight: 1.2,
                      marginTop: 6,
                    }}
                  >
                    {doc.title}
                  </Link>
                </>
              ) : (
                <>
                  <PdfIcon />
                  <Text
                    style={{
                      fontSize: 8,
                      textAlign: "center",
                      lineHeight: 1.2,
                      marginTop: 6,
                    }}
                  >
                    {doc.title}
                  </Text>
                </>
              )}
            </View>
          ))
        ) : (
          <Text style={styles.tableCell}>{t("No legal documents found.")}</Text>
        )}
      </View>

      <View style={styles.note}>
        <HandIcon />

        <View style={styles.noteTextContainer}>
          <Text style={{ fontWeight: 600, marginBottom: 4 }}>
            {t("Important Notes:")}
          </Text>

          {notes.map((line, idx) => (
            <Text key={idx} style={{ marginBottom: 3 }}>
              {t(line)}
            </Text>
          ))}
        </View>
      </View>
    </View>
  );
};

export default withNamespaces()(LegalDocuments);
